import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";
import { LuSearch } from "react-icons/lu";
import { FaRegEdit } from "react-icons/fa";
import { FaRegTrashCan } from "react-icons/fa6";
import addIcon from "../assets/images/icons/user1.svg";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    company_name: "",
    phone: "",
    is_active: true,
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log("Fetching users...");
      console.log(
        "Current auth header:",
        axios.defaults.headers.common["Authorization"]
      );
      const response = await axios.get("/api/users/");
      console.log("Users fetched successfully:", response.data);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      console.error("Error response:", error.response?.data);
      setError(
        "Failed to fetch users: " +
          (error.response?.data?.detail || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear any previous errors
    try {
      if (editingUser) {
        // Update user - remove password if empty
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        delete updateData.is_active; // Don't allow status change via this form

        await axios.put(`/api/users/${editingUser.id}`, updateData);
        setSuccess("User updated successfully");
      } else {
        // Create user
        const createData = { ...formData };
        delete createData.is_active; // Don't send is_active for creation

        await axios.post("/api/users/", createData);
        setSuccess("User created successfully");
      }

      fetchUsers(); // Refresh the list
      handleCloseModal();
    } catch (error) {
      setError(
        "Failed to save user: " +
          (error.response?.data?.detail || error.message)
      );
      console.error("Error saving user:", error);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`/api/users/${userId}`);
        setUsers(users.filter((user) => user.id !== userId));
        setSuccess("User deleted successfully");
      } catch (error) {
        setError(
          "Failed to delete user: " +
            (error.response?.data?.detail || error.message)
        );
        console.error("Error deleting user:", error);
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      password: "",
      company_name: user.company_name || "",
      phone: user.phone || "",
      is_active: user.is_active,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      full_name: "",
      email: "",
      password: "",
      company_name: "",
      phone: "",
      is_active: true,
    });
    setError("");
  };

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="d-flex justify-content-between align-items-center">
          <h3>User Management</h3>
          <div className="right-side-header">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search users..."
                className="form-control"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" className="search-btn">
                <LuSearch size={20} />
              </button>
            </div>
            <Button
              variant="dark"
              className="btn-primary"
              onClick={() => {
                setError(""); // Clear any previous errors
                setShowModal(true);
              }}
            >
              <span className="btn-icon">
                <img src={addIcon} alt="add" />{" "}
              </span>
              Add New User
            </Button>
          </div>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <Alert
          variant="danger"
          onClose={() => setError("")}
          dismissible
          className="mb-4"
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          variant="success"
          onClose={() => setSuccess("")}
          dismissible
          className="mb-4"
        >
          {success}
        </Alert>
      )}

      <div className="main-layout">
        <Table responsive hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.full_name}</td>
                  <td>{user.email}</td>
                  <td>{user.company_name || "-"}</td>
                  <td>{user.phone || "-"}</td>
                  <td>
                    <Badge bg={user.is_active ? "success" : "secondary"}>
                      {user.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <Button
                      className="action-btn me-3"
                      size="md"
                      variant="outline"
                      onClick={() => handleEdit(user)}
                    >
                      <FaRegEdit />
                    </Button>
                    <Button
                      variant="outline"
                      className="action-btn"
                      size="md"
                      onClick={() => handleDelete(user.id)}
                    >
                      <FaRegTrashCan />
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  <div className="text-muted">
                    {searchTerm ? (
                      <>
                        <i className="fas fa-search mb-2" style={{ fontSize: '2rem' }}></i>
                        <br />
                        No users found matching "{searchTerm}"
                        <br />
                        <small>Try adjusting your search terms</small>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-users mb-2" style={{ fontSize: '2rem' }}></i>
                        <br />
                        No users found
                        <br />
                        <small>Start by adding your first user</small>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Add/Edit User Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? "Edit User" : "Add New User"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.full_name}
                    onChange={(e) =>
                      setFormData({ ...formData, full_name: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Password{" "}
                    {editingUser ? "(leave blank to keep current)" : "*"}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required={!editingUser}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.company_name}
                    onChange={(e) =>
                      setFormData({ ...formData, company_name: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUser ? "Update User" : "Create User"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};



export default UserManagement;
